import { Navigate } from 'react-router-dom';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { useBlueprints } from '@/hooks/blueprints/useBlueprints';
import { useOrgRoutes } from '@/lib/useOrgRoutes';

/**
 * Compatibility shim for the legacy `/:org/blueprints/base-image` URL.
 *
 * The standalone Base Image page was merged into `BlueprintDetailPanel`
 * — base image management is now reachable by selecting the host-managed
 * `base` blueprint from the regular blueprints sidebar. This component
 * resolves the base blueprint's id from the live list and redirects to
 * its detail page so old bookmarks, navigation links, and any external
 * deep-links keep working without a 404.
 */
export function BaseImageRedirect() {
  const routes = useOrgRoutes();
  const { data: blueprints = [], isLoading } = useBlueprints();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Spinner className="size-5" />
      </div>
    );
  }

  const baseBlueprint = blueprints.find((bp) => bp.name === 'base');
  if (!baseBlueprint) {
    // No base blueprint exists yet (Runloop hasn't completed the
    // initial bootstrap, or this org has no `RUNLOOP_API_KEY`). Fall
    // back to the list page instead of stranding the user on a 404.
    return <Navigate to={routes.blueprints} replace />;
  }

  return <Navigate to={routes.blueprint(baseBlueprint.id)} replace />;
}
